import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectCustomerType } from '@econtractor/types';
import * as apiObject from 'api/apiObjects';
import { useToast } from 'context/ToastContext';
import useCustomerAiInfo from '../../model/useCustomerAiInfo';
import { mergeCustomerFromAi } from '../../model/customerAiUtils';
import {
    EMPTY_CUSTOMER,
    mapYouControlToCustomer,
    type YouControlUsr,
} from './customerFormUtils';

interface UseCustomerFormFieldsParams {
    initialCustomer?: ObjectCustomerType;
    resetKey?: string | number;
    readOnly?: boolean;
    edrpouReadOnly?: boolean;
}

const useCustomerFormFields = ({
    initialCustomer,
    resetKey,
    readOnly = false,
    edrpouReadOnly = false,
}: UseCustomerFormFieldsParams = {}) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const [formValues, setFormValues] = useState<ObjectCustomerType>({ ...EMPTY_CUSTOMER });
    const [isSaving, setIsSaving] = useState(false);
    const [isYcBusy, setIsYcBusy] = useState(false);
    const initialRef = useRef(initialCustomer);
    initialRef.current = initialCustomer;

    const {
        refInputFile,
        isBusy: isAiBusy,
        uploadProgress,
        phase: aiPhase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    } = useCustomerAiInfo({
        onParsed: (parsed) =>
            setFormValues((prev) => {
                const next = mergeCustomerFromAi(prev, parsed);
                if (edrpouReadOnly) next.edrpouOrIpn = prev.edrpouOrIpn;
                return next;
            }),
    });

    useEffect(() => {
        setFormValues({ ...EMPTY_CUSTOMER, ...(initialRef.current || {}) });
    }, [resetKey]);

    const handleChange = <K extends keyof ObjectCustomerType>(field: K, value: ObjectCustomerType[K]) => {
        if (edrpouReadOnly && field === 'edrpouOrIpn') return;
        setFormValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleFetchYouControl = async () => {
        const companyCode = String(formValues.edrpouOrIpn || '').trim();
        if (companyCode.length < 5) {
            showToast(t('customer.youControl.messCodeRequired'), 'warning');
            return;
        }
        if (isYcBusy || isSaving || isAiBusy || readOnly) return;

        setIsYcBusy(true);
        const ycData = await apiObject.getYouControlCompany(companyCode);
        setIsYcBusy(false);

        if (!ycData) {
            showToast(t('customer.youControl.messError'), 'danger');
            return;
        }

        const mapped = mapYouControlToCustomer(ycData as YouControlUsr, companyCode);
        setFormValues((prev) => {
            const next = mergeCustomerFromAi(prev, mapped);
            if (edrpouReadOnly) next.edrpouOrIpn = prev.edrpouOrIpn;
            return next;
        });
        showToast(t('customer.youControl.messLoaded'), 'success');
    };

    const yesLabel = t('customer.fields.vatYes');
    const noLabel = t('customer.fields.vatNo');
    const ycCodeReady = String(formValues.edrpouOrIpn || '').trim().length >= 5;

    const getPersonTypeLabel = (value?: string) => {
        if (value === 'legal') return t('customer.personTypes.legal');
        if (value === 'individual') return t('customer.personTypes.individual');
        return value || '-';
    };

    return {
        t,
        formValues,
        setFormValues,
        readOnly,
        edrpouReadOnly,
        isSaving,
        setIsSaving,
        isYcBusy,
        isAiBusy,
        ycCodeReady,
        yesLabel,
        noLabel,
        getPersonTypeLabel,
        handleChange,
        handleFetchYouControl,
        refInputFile,
        uploadProgress,
        aiPhase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    };
};

export default useCustomerFormFields;
